/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import logo from '../asset/logo.svg';
import { db } from '../config/firebaseConfig';
import { UserAuth } from '../context/authContext';
import './Sidebar.css';

const Sidebar = () => {
  const user = UserAuth();
  const [roomId, setRoomId] = useState(localStorage.getItem('roomId') || '');
  const [role, setRole] = useState(localStorage.getItem('role') || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [roomData, setRoomData] = useState(null);
  const navigate = useNavigate();

  // Listen to room document for AstraPOS game state
  useEffect(() => {
    if (!roomId) {
      setRoomData(null);
      return;
    }

    const roomRef = doc(db, 'rooms', roomId);
    const unsubscribe = onSnapshot(
      roomRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setRoomData(snapshot.data());
        } else {
          setRoomData(null);
        }
      },
      (error) => {
        console.error('Error listening to room:', error);
      }
    );

    return () => unsubscribe();
  }, [roomId]);

  // Sync role from room data (miners/participants arrays) to stay in sync with current game state
  useEffect(() => {
    if (!roomData || !user?.publicKey) return;

    // Check if user is a miner or participant in current game state
    const isMinerInCurrent = roomData.miners?.includes(user.publicKey);
    const isParticipantInCurrent = roomData.participants?.includes(
      user.publicKey
    );

    let newRole = '';
    if (isMinerInCurrent) {
      newRole = 'validator';
    } else if (isParticipantInCurrent) {
      newRole = 'participant';
    }

    // Update both state and localStorage to keep them in sync
    setRole(newRole);
    if (newRole) {
      localStorage.setItem('role', newRole);
    }
  }, [roomData, user?.publicKey]);

  const toggleSidebar = () => {
    setIsVisible(!isVisible);
  };

  // Helper functions for AstraPOS game state
  const isAstraPos = roomData?.roomType === 'ASTRAPOS';
  const gameStage = roomData?.gameState?.stage;
  const isMiner = role === 'validator';
  const isParticipant = role === 'participant';

  // Determine if player is in active game phase
  const isInGamePhase = roomId && gameStage === 'PLAYING';

  // Tab visibility logic
  // When NOT in game phase: only show Dashboard, Game, Home tabs
  // When in PLAYING stage: show all tabs including Mempool, Blocks, Transactions, etc.
  const shouldShowRoomDashboard = isInGamePhase;
  const shouldShowCreateTx = isInGamePhase;
  const shouldShowLeaderboard = isInGamePhase;
  const shouldShowLobby = isInGamePhase;
  const shouldShowExplore = isInGamePhase;
  const shouldShowMempool = isMiner && isInGamePhase;
  const shouldShowTransaction = isInGamePhase;
  const shouldShowMyTransactions = isInGamePhase;
  const shouldShowBlocks = isMiner && isInGamePhase;
  const shouldShowGameTab = isAstraPos && roomId;

  return (
    <>
      {/* Toggle Button */}
      <button
        className={`sidebar-toggle-btn ${!isVisible ? 'sidebar-hidden' : ''}`}
        onClick={toggleSidebar}
        aria-label={isVisible ? 'Hide sidebar' : 'Show sidebar'}
        title={isVisible ? 'Hide sidebar' : 'Show sidebar'}
      ></button>

      {/* Sidebar */}
      <aside
        className={`modern-sidebar ${isExpanded ? 'expanded' : ''} ${!isVisible ? 'hidden' : ''}`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        <div className="sidebar-content">
          <nav className="sidebar-nav">
            <ul className="nav-list">
              <li className="nav-item logo-item">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    isActive ? 'nav-link active' : 'nav-link'
                  }
                >
                  <div className="nav-icon logo-icon">
                    <img src={logo} alt="AstraPOS logo" className="logo" />
                  </div>
                  <span className="nav-text">Home</span>
                </NavLink>
              </li>

              {user && (
                <li className="nav-item">
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13 3C13 2.73478 12.8946 2.48043 12.7071 2.29289C12.5196 2.10536 12.2652 2 12 2C11.7348 2 11.4804 2.10536 11.2929 2.29289C11.1054 2.48043 11 2.73478 11 3V4H10C9.73478 4 9.48043 4.10536 9.29289 4.29289C9.10536 4.48043 9 4.73478 9 5V8H6C5.73478 8 5.48043 8.10536 5.29289 8.29289C5.10536 8.48043 5 8.73478 5 9V11H3C2.73478 11 2.48043 11.1054 2.29289 11.2929C2.10536 11.4804 2 11.7348 2 12C2 12.2652 2.10536 12.5196 2.29289 12.7071C2.48043 12.8946 2.73478 13 3 13H5V15C5 15.2652 5.10536 15.5196 5.29289 15.7071C5.48043 15.8946 5.73478 16 6 16H9V19C9 19.2652 9.10536 19.5196 9.29289 19.7071C9.48043 19.8946 9.73478 20 10 20H11V21C11 21.2652 11.1054 21.5196 11.2929 21.7071C11.4804 21.8946 11.7348 22 12 22C12.2652 22 12.5196 21.8946 12.7071 21.7071C12.8946 21.5196 13 21.2652 13 21V20H14C14.2652 20 14.5196 19.8946 14.7071 19.7071C14.8946 19.5196 15 19.2652 15 19V16H18C18.2652 16 18.5196 15.8946 18.7071 15.7071C18.8946 15.5196 19 15.2652 19 15V13H21C21.2652 13 21.5196 12.8946 21.7071 12.7071C21.8946 12.5196 22 12.2652 22 12C22 11.7348 21.8946 11.4804 21.7071 11.2929C21.5196 11.1054 21.2652 11 21 11H19V9C19 8.73478 18.8946 8.48043 18.7071 8.29289C18.5196 8.10536 18.2652 8 18 8H15V5C15 4.73478 14.8946 4.48043 14.7071 4.29289C14.5196 4.10536 14.2652 4 14 4H13V3ZM11 6H13V8H11V6ZM7 10H9V8H11V10H13V12H15V10H17V13H15V15H13V13H11V15H9V13H7V10Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">Dashboard</span>
                  </NavLink>
                </li>
              )}

              {!user && (
                <li className="nav-item">
                  <NavLink
                    to="/login"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.0865 22C11.9627 22 11.8388 21.9716 11.7271 21.9137L8.12599 20.0496C7.10415 19.5201 6.30481 18.9259 5.68063 18.2336C4.31449 16.7195 3.5544 14.776 3.54232 12.7599L3.50004 6.12426C3.495 5.35842 3.98931 4.67103 4.72826 4.41215L11.3405 2.10679C11.7331 1.96656 12.1711 1.9646 12.5707 2.09992L19.2081 4.32684C19.9511 4.57493 20.4535 5.25742 20.4575 6.02228L20.4998 12.6628C20.5129 14.676 19.779 16.6274 18.434 18.1581C17.8168 18.8602 17.0245 19.4632 16.0128 20.0025L12.4439 21.9088C12.3331 21.9686 12.2103 21.999 12.0865 22Z"
                          fill="currentColor"
                          opacity="0.5"
                        />
                        <path
                          d="M11.3194 14.3209C11.1261 14.3219 10.9328 14.2523 10.7838 14.1091L8.86695 12.2656C8.57097 11.9793 8.56795 11.5145 8.86091 11.2262C9.15387 10.9369 9.63207 10.934 9.92906 11.2193L11.3083 12.5451L14.6758 9.22479C14.9698 8.93552 15.448 8.93258 15.744 9.21793C16.041 9.50426 16.044 9.97004 15.751 10.2574L11.8519 14.1022C11.7049 14.2474 11.5127 14.3199 11.3194 14.3209Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">Login</span>
                  </NavLink>
                </li>
              )}

              {shouldShowGameTab && (
                <li className="nav-item">
                  <NavLink
                    to="/game"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 8H4C2.9 8 2 8.9 2 10V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V10C22 8.9 21.1 8 20 8ZM12 14C10.9 14 10 13.1 10 12C10 10.9 10.9 10 12 10C13.1 10 14 10.9 14 12C14 13.1 13.1 14 12 14ZM18 15L16 12L13 16H8L5 11V10H18V15Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">Game</span>
                  </NavLink>
                </li>
              )}

              {roomId && !isAstraPos && (
                <li className="nav-item">
                  <NavLink
                    to="/joinedroom"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.0865 23.26C7.65995 22.939 5.40518 21.8326 3.66651 20.1098C1.92784 18.387 0.800835 16.1424 0.457616 13.7188C0.114397 11.2953 0.574174 8.826 1.76608 6.68812C2.95798 4.55024 4.81686 2.86122 7.05884 1.88C7.30444 1.78307 7.57808 1.78527 7.8221 1.88611C8.06612 1.98695 8.26148 2.17855 8.367 2.4206C8.47253 2.66262 8.47998 2.93616 8.38781 3.18359C8.29564 3.43101 8.111 3.63301 7.87284 3.747C6.49934 4.34344 5.29295 5.26738 4.35921 6.43801C3.42548 7.60864 2.79287 8.99027 2.51673 10.462C2.2406 11.9337 2.32935 13.4507 2.77523 14.8802C3.22111 16.3097 4.01052 17.6081 5.0744 18.6619C6.13828 19.7156 7.44419 20.4926 8.87787 20.9248C10.3116 21.357 11.8293 21.4312 13.2983 21.141C14.7673 20.8509 16.1429 20.2051 17.3045 19.2602C18.4662 18.3153 19.3786 17.1001 19.9618 15.721C20.0133 15.5972 20.0888 15.4847 20.1839 15.3902C20.279 15.2956 20.3919 15.2208 20.516 15.17C20.6401 15.1192 20.7731 15.0935 20.9072 15.0944C21.0413 15.0952 21.1739 15.1225 21.2975 15.1748C21.421 15.2271 21.5329 15.3033 21.6268 15.399C21.7208 15.4948 21.7948 15.6081 21.8448 15.7326C21.8947 15.8571 21.9196 15.9902 21.9179 16.1243C21.9162 16.2584 21.8879 16.3908 21.8348 16.514C20.8864 18.7702 19.2232 20.6525 17.1009 21.8715C14.9786 23.0905 12.5146 23.5786 10.0878 23.261H10.0865ZM11.5346 13.173C11.401 13.173 11.2687 13.1467 11.1452 13.0955C11.0217 13.0443 10.9095 12.9693 10.815 12.8748C10.7206 12.7802 10.6456 12.668 10.5946 12.5445C10.5436 12.421 10.5174 12.2886 10.5175 12.155V1.96798C10.5174 1.83429 10.5436 1.70189 10.5946 1.57833C10.6456 1.45477 10.7206 1.34248 10.815 1.24786C10.9094 1.15324 11.0216 1.07813 11.145 1.02685C11.2685 0.97558 11.4009 0.94914 11.5346 0.948998C14.5035 0.952974 17.3493 2.13554 19.4467 4.23681C21.544 6.33809 22.7212 9.18613 22.7196 12.155C22.7198 12.2886 22.6936 12.421 22.6425 12.5445C22.5915 12.668 22.5166 12.7802 22.4221 12.8748C22.3277 12.9693 22.2155 13.0443 22.0921 13.0955C21.9686 13.1467 21.8363 13.173 21.7026 13.173H11.5346ZM12.5516 11.136H20.6296C20.4007 9.07081 19.4764 7.14486 18.0085 5.67425C16.5406 4.20364 14.6164 3.27578 12.5516 3.04299V11.136Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">Room Dashboard</span>
                  </NavLink>
                </li>
              )}

              {shouldShowCreateTx && (
                <li className="nav-item">
                  <NavLink
                    to="/create"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 12L16 16M20 12L16 8M20 12H9M9 3C5.68629 3 3 5.68629 3 9V15C3 18.3137 5.68629 21 9 21"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">Create Transaction</span>
                  </NavLink>
                </li>
              )}

              {shouldShowLeaderboard && (
                <li className="nav-item">
                  <NavLink
                    to="/leaderboard"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16 11V3H8V9H2V21H22V11H16ZM10 5H14V19H10V5ZM4 11H8V19H4V11ZM20 19H16V13H20V19Z"
                          fill="currentColor"
                        />
                        <path d="M11 1L9 3H15L13 1H11Z" fill="currentColor" />
                      </svg>
                    </div>
                    <span className="nav-text">Leaderboard</span>
                  </NavLink>
                </li>
              )}

              {shouldShowLobby && (
                <li className="nav-item">
                  <NavLink
                    to="/lobby"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 12C10.93 12 12.5 10.43 12.5 8.5C12.5 6.57 10.93 5 9 5C7.07 5 5.5 6.57 5.5 8.5C5.5 10.43 7.07 12 9 12ZM9 14C6.33 14 1 15.34 1 18V20H17V18C17 15.34 11.67 14 9 14ZM16.76 14.06C17.78 14.66 19 15.7 19 18V20H23V18C23 15.82 19.43 14.53 16.76 14.06ZM15 12C16.93 12 18.5 10.43 18.5 8.5C18.5 6.57 16.93 5 15 5C14.46 5 13.96 5.13 13.5 5.35C14.13 6.24 14.5 7.33 14.5 8.5C14.5 9.67 14.13 10.76 13.5 11.65C13.96 11.87 14.46 12 15 12Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">Lobby</span>
                  </NavLink>
                </li>
              )}

              {shouldShowExplore && (
                <li className="nav-item">
                  <NavLink
                    to="/explore"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15 8C15 9.65685 13.6569 11 12 11C10.3431 11 9 9.65685 9 8C9 6.34315 10.3431 5 12 5C13.6569 5 15 6.34315 15 8Z"
                          fill="currentColor"
                        />
                        <path
                          d="M6 15C6 16.1046 5.10457 17 4 17C2.89543 17 2 16.1046 2 15C2 13.8954 2.89543 13 4 13C5.10457 13 6 13.8954 6 15Z"
                          fill="currentColor"
                        />
                        <path
                          d="M20 17C21.1046 17 22 16.1046 22 15C22 13.8954 21.1046 13 20 13C18.8954 13 18 13.8954 18 15C18 16.1046 18.8954 17 20 17Z"
                          fill="currentColor"
                        />
                        <path
                          d="M10.5351 9.56434L5.85355 13.1464"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M13.4645 9.56434L18.1464 13.1464"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M12 19C13.6569 19 15 17.6569 15 16C15 14.3431 13.6569 13 12 13C10.3431 13 9 14.3431 9 16C9 17.6569 10.3431 19 12 19Z"
                          fill="currentColor"
                        />
                        <path
                          d="M5.46447 15.5355L9.87868 16.1213"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M18.5355 15.5355L14.1213 16.1213"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">Explore Network</span>
                  </NavLink>
                </li>
              )}

              {shouldShowMempool && (
                <li className="nav-item">
                  <NavLink
                    to="/mempool"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="22"
                        height="26"
                      >
                        <path
                          d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 5V7H5V5H19ZM5 19V9H19V19H5Z"
                          fill="currentColor"
                        />
                        <rect
                          x="7"
                          y="11"
                          width="10"
                          height="1.5"
                          fill="currentColor"
                          opacity="0.7"
                        />
                        <rect
                          x="7"
                          y="14"
                          width="8"
                          height="1.5"
                          fill="currentColor"
                          opacity="0.7"
                        />
                        <rect
                          x="7"
                          y="17"
                          width="6"
                          height="1.5"
                          fill="currentColor"
                          opacity="0.7"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">Mempool</span>
                  </NavLink>
                </li>
              )}

              {shouldShowBlocks && (
                <li className="nav-item">
                  <NavLink
                    to="/blocks"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">Blocks</span>
                  </NavLink>
                </li>
              )}

              {shouldShowTransaction && (
                <li className="nav-item">
                  <NavLink
                    to="/transaction"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M19 3H5C3.9 3 3.01 3.9 3.01 5L3 19C3 20.1 3.89 21 4.99 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z"
                          fill="currentColor"
                        />
                        <path d="M7 7H17V9H7V7Z" fill="currentColor" />
                        <path d="M7 11H17V13H7V11Z" fill="currentColor" />
                        <path d="M7 15H14V17H7V15Z" fill="currentColor" />
                      </svg>
                    </div>
                    <span className="nav-text">Transaction</span>
                  </NavLink>
                </li>
              )}

              {shouldShowMyTransactions && (
                <li className="nav-item">
                  <NavLink
                    to="/my-transactions"
                    className={({ isActive }) =>
                      isActive ? 'nav-link active' : 'nav-link'
                    }
                  >
                    <div className="nav-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <span className="nav-text">My Transactions</span>
                  </NavLink>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
